import React, { use, useCallback, useEffect, useMemo } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import type { CompositeScreenProps } from "@react-navigation/native";
import type { BottomTabScreenProps } from "@react-navigation/bottom-tabs";
import type { StackScreenProps } from "@react-navigation/stack";
import { useTheme } from "../../hooks/use-theme";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useQuery } from "@apollo/client/react";
import Button from "../../components/Button";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import Loading from "../../components/Loading";
import { getHeaderTitle } from "@react-navigation/elements";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { WorkerTabParamList } from "../../navigations/worker/Home";
import NoActivitiesImage from "../../assets/images/no-activities-image.svg";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import ScreenCard from "../ScreenCard";
import {
    ProfileType,
    TerminationInfo,
    ContractState,
    ContractSignatureStatus,
} from "../../__generated__/graphql";
import { ListGroupItem, useListGroupRenderItem } from "../../components/ListGroup";
import { useMissionDisplayAttributes } from "../../hooks/missions";
import EmptyScreen from "../../components/EmptyScreen";
import { useTracking } from "../../hooks/tracking";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { SHORT_DATE_IN_LIST_FORMAT_OPTIONS, useDateFormatter } from "../../hooks/date";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import { useBusinessEmailVerificationHeadband } from "../../hooks/use-business-email-verification-headband";
import { LinkSmallTextBold } from "../../components/FormattedText";
import { useIntercom } from "../../hooks/intercom";
import Gap from "../../ui_atoms/Gap";
import { getMissionJobTitleDisplay } from "../../utils";
import { FilterOption, useTypeFilters } from "../../hooks/use-type-filters";
import { CoreMissionFieldsFragment } from "../../__generated__/operations/graphql";
import ScreenHeader, { HeaderButton } from "./ScreenHeader";
import { SelectChooser } from "../../components/select/SelectChooser";
import { useScreenState } from "../../hooks/use-screen-state";
import ListItem from "../../ui_atoms/ListItem";
import ListAnnotation from "../../ui_atoms/ListAnnotation";
import { useAtom } from "jotai";
import { missionsSigningLaunched } from "../../atoms/missions";
import Switch from "../../ui_atoms/Switch";
import Icon from "../../components/Icon";

// Static filter config for the missions list. Hoisted so the references stay stable across
// renders — passing them inline to useTypeFilters would rebuild `filteredMissions` (and thus
// the whole `items` array) on every render, e.g. on every scroll-driven re-render.
const MISSIONS_DEFAULT_FILTER_VALUES = [
    ContractState.Accepted,
    ContractState.Draft,
    ContractState.Ended,
    ContractState.PendingAcceptation,
    ContractState.Terminated,
];

const MISSIONS_FILTER_SORT_ORDER = [
    ContractState.Accepted,
    ContractState.Ended,
    ContractState.PendingAcceptation,
    ContractState.Cancelled,
    ContractState.Terminated,
];

// Signature statuses that mean the contract is awaiting the current user's own signature —
// both are resolved server-side relative to the querying profile.
const SELF_SIGNABLE_SIGNATURE_STATUSES = [
    ContractSignatureStatus.NeedSelfSign,
    ContractSignatureStatus.WaitingSelfSign,
];

const SELECTION_CHECKBOX_SIZE = 26;
// ListItem's big avatar, which the checkbox is vertically centred on.
const SELECTION_ROW_AVATAR_SIZE = 48;

const getMissionFilterValue = (mission: CoreMissionFieldsFragment) =>
    mission.currentContract?.state || ContractState.Draft;

type EmptyMissionsProps = {
    onCreateMission: () => void;
    onInviteClient: () => void;
    hasTalent: boolean;
    hasClient: boolean;
};

const EmptyMissions: React.FC<EmptyMissionsProps> = ({
    onCreateMission,
    onInviteClient,
    hasTalent,
    hasClient,
}) => {
    const styles = useStyles();
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const isLarge = useIsScreenSizeMin("large");

    const { trackEvent } = useTracking();

    useEffect(() => {
        if (profileType) {
            void trackEvent("show_empty_mission_list", { profileType }, true);
        }
    }, [trackEvent, profileType]);

    return (
        <>
            <EmptyScreen
                image={NoActivitiesImage}
                text={i18n.t(
                    profileType === ProfileType.Client
                        ? hasTalent
                            ? "screens.user.missions.create_mission_now"
                            : `screens.user.missions.no_talents${isLarge ? "_left" : ""}`
                        : hasClient
                          ? "screens.user.missions.has_invites"
                          : "screens.user.missions.no_invites",
                )}
            />
            <Gap gap={1} />
            {profileType === ProfileType.Client && (
                <Button
                    size="large"
                    testID="button-new-mission"
                    style={styles.startButton}
                    onPress={onCreateMission}
                    label={i18n.t("screens.user.missions.create_mission")}
                />
            )}
            {profileType === ProfileType.Worker && !hasClient && (
                <Button
                    size="large"
                    testID="button-invite-client"
                    buttonMode="secondary"
                    style={styles.startButton}
                    onPress={onInviteClient}
                    label={i18n.t("screens.user.missions.invite_client")}
                />
            )}
        </>
    );
};

type MissionsScreenProps = CompositeScreenProps<
    BottomTabScreenProps<WorkerTabParamList, "MissionsScreen">,
    StackScreenProps<RootStackParamList>
>;

export default function MissionsScreen() {
    const styles = useStyles();
    const { colors } = useTheme();
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { user, refreshUser } = use(AuthManagerContext);
    const { presentArticleId } = useIntercom();
    const isLarge = useIsScreenSizeMin("large");
    const emailVerificationHeadband = useBusinessEmailVerificationHeadband();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isSelectionMode, setIsSelectionMode] = React.useState(false);
    const [selectedMissionIds, setSelectedMissionIds] = React.useState<string[]>([]);

    const {
        loading: missionsLoading,
        error: missionsError,
        data: missionsData,
        refetch: missionsRefetch,
    } = useQuery(FIND_MY_MISSIONS, {
        skip: !profileType,
    });

    // https://reactnavigation.org/docs/typescript/#annotating-usenavigation. But typescript
    // annotation is not working well so I had to add ["navigation"] to make it work
    const navigation = useNavigation<MissionsScreenProps["navigation"]>();

    const refresh = useCallback(async () => {
        // refresh user is required to update the clientProfile.clientRelationshipCount
        return await Promise.all([refreshUser?.(), missionsRefetch()]);
    }, [missionsRefetch, refreshUser]);

    const { trackEvent } = useTracking();

    const clientProfile = useCurrentProfile();

    const allMissions = useMemo(
        () => missionsData?.findMyMissions?.elements || undefined,
        [missionsData?.findMyMissions?.elements],
    );

    const hasTalent = useMemo(
        () => (clientProfile?.clientRelationshipCount || 0) > 0,
        [clientProfile?.clientRelationshipCount],
    );

    const hasClient = useMemo(
        () => (user?.workerProfile?.relationshipCount || 0) > 0,
        [user?.workerProfile?.relationshipCount],
    );

    const isClient = profileType === ProfileType.Client;
    const intDateFormatter = useDateFormatter();

    const formatDate = useCallback(
        (d?: string | null, empty = "") => {
            if (!d) {
                return empty;
            }
            return intDateFormatter(new Date(d), SHORT_DATE_IN_LIST_FORMAT_OPTIONS);
        },
        [intDateFormatter],
    );

    const onCreateMission = React.useCallback(() => {
        void trackEvent("click_create_mission", {}, true);
        if (isClient) {
            navigation.navigate("CreateMissionStack");
        }
    }, [isClient, navigation, trackEvent]);

    const onInviteClient = React.useCallback(() => {
        navigation.navigate("InviteClientInfo");
    }, [navigation]);

    const onSelectMission = React.useCallback(
        (missionId: string) => {
            navigation.navigate("MissionDetails", { missionId });
        },
        [navigation],
    );

    const enterSelectionMode = useCallback(() => {
        setIsMenuOpen(false);
        setIsSelectionMode(true);
    }, []);

    const exitSelectionMode = useCallback(() => {
        setIsSelectionMode(false);
        setSelectedMissionIds([]);
    }, []);

    const toggleMissionSelection = useCallback((missionId: string) => {
        setSelectedMissionIds((prev) =>
            prev.includes(missionId) ? prev.filter((id) => id !== missionId) : [...prev, missionId],
        );
    }, []);

    // Once the signature has been scheduled the selection is spent: leave selection mode so
    // closing the flow lands back on the plain list.
    const [signingLaunched, setSigningLaunched] = useAtom(missionsSigningLaunched);
    useEffect(() => {
        if (signingLaunched) {
            setSigningLaunched(false);
            exitSelectionMode();
        }
    }, [signingLaunched, setSigningLaunched, exitSelectionMode]);

    const getMissionDisplayAttributes = useMissionDisplayAttributes();

    const isInitialLoading = !missionsData && missionsLoading;

    const getFilterLabel = useCallback(
        (value: ContractState, quantity: number) =>
            `${i18n.t(`screens.user.missions.filters.${value}`)} (${quantity})`,
        [i18n],
    );

    const {
        filteredItems: filteredMissions,
        availableFilterOptions,
        activeFilters,
    } = useTypeFilters({
        items: allMissions,
        getFilterValue: getMissionFilterValue,
        defaultFilterValues: MISSIONS_DEFAULT_FILTER_VALUES,
        getLabel: getFilterLabel,
        sortOrder: MISSIONS_FILTER_SORT_ORDER,
    });

    // Only contracts awaiting the current user's own signature can be batch-signed.
    const signableMissionIds = useMemo(
        () =>
            new Set(
                (filteredMissions ?? [])
                    .filter(
                        (mission) =>
                            !!mission.currentContract?.signatureStatus &&
                            SELF_SIGNABLE_SIGNATURE_STATUSES.includes(
                                mission.currentContract.signatureStatus,
                            ),
                    )
                    .map((mission) => mission.id),
            ),
        [filteredMissions],
    );

    // Batch signing is a client-side action, and pointless without anything to sign.
    const canMultiSign = isClient && signableMissionIds.size > 0;

    // The default selection: the available options that are active by default. Stored as option
    // objects because useScreenState mirrors their `value`s into the "filters" route param, which
    // useTypeFilters reads back to compute `checked` and `filteredItems`.
    const defaultFilterOptions = useMemo(
        () =>
            availableFilterOptions.filter((option) =>
                MISSIONS_DEFAULT_FILTER_VALUES.includes(option.value),
            ),
        [availableFilterOptions],
    );

    const [, setActiveFilters] = useScreenState("filters", defaultFilterOptions);

    // Toggle from the displayed (param-derived) selection, not from useScreenState's internal
    // state: on a mount with a pre-existing "filters" param (web reload, shared URL) that state
    // still holds the defaults, and a first tap based on it would scramble the selection.
    const toggleFilter = useCallback(
        (option: FilterOption<ContractState>) => {
            setActiveFilters(
                option.checked
                    ? activeFilters.filter((filter) => filter.value !== option.value)
                    : [...activeFilters, option],
            );
        },
        [activeFilters, setActiveFilters],
    );

    const filterItems = useMemo(
        () =>
            availableFilterOptions.map((option) => {
                // Keep at least one filter on, as the legacy filter dropdown did: turning the last
                // one off would drop the screen to its "no missions" empty state, which reads as an
                // error rather than as an over-narrowed filter. The rule is enforced by disabling
                // the row, so the greyed-out switch shows why the press does nothing.
                const isLastActiveFilter = option.checked && activeFilters.length === 1;

                return {
                    selectable: false,
                    value: option.value,
                    // SelectItemType requires a label, which its row renderer turns into the item's
                    // title. Empty here so the text renders once, through `description` only.
                    label: "",
                    description: option.label,
                    rightActionMode: "none" as const,
                    disabled: isLastActiveFilter,
                    // The row itself handles the press, so the switch is display-only: letting it
                    // take the touch would compete with the row and toggle the filter twice.
                    badge: (
                        <View pointerEvents="none">
                            <Switch value={option.checked} disabled={isLastActiveFilter} />
                        </View>
                    ),
                    onPress: () => toggleFilter(option),
                };
            }),
        [availableFilterOptions, activeFilters.length, toggleFilter],
    );

    // SelectChooser pads its header by marginSize, which the negative margin cancels: the block
    // then lines up with the filter rows below, and the separator spans the whole sheet width.
    const filtersTopComponent = useMemo(
        () => (
            <View style={styles.filtersHeader}>
                {canMultiSign && (
                    <>
                        <ListItem
                            description={i18n.t("screens.user.missions.multi_sign_title")}
                            labels={[
                                {
                                    text: i18n.t("screens.user.missions.multi_sign_description"),
                                    mode: "miniCtaGrey",
                                },
                            ]}
                            globalTextColor={colors.secondary}
                            // descriptionColor={colors.onSurfaceDisabled}
                            rightActionMode="none"
                            inGroup
                            onPress={enterSelectionMode}
                        />
                        <View style={styles.filtersSeparator} />
                    </>
                )}
                <ListAnnotation
                    value={i18n.t("screens.user.missions.filters_intro")}
                    style={styles.filtersIntro}
                />
            </View>
        ),
        [
            i18n,
            canMultiSign,
            colors.secondary,
            enterSelectionMode,
            styles.filtersHeader,
            styles.filtersSeparator,
            styles.filtersIntro,
        ],
    );

    const filtersBottomComponent = useMemo(
        () => (
            <Button
                label={i18n.t("close")}
                buttonMode="outlined"
                size="medium"
                onPress={() => {
                    setIsMenuOpen(false);
                }}
            />
        ),
        [i18n],
    );

    // No filter action while there is nothing to filter (loading, error, no missions) — parity
    // with the legacy filterComponent, which rendered nothing without options. Opening the sheet
    // with zero items would present an empty, touch-blocking overlay.
    const hasFilterOptions = availableFilterOptions.length > 0;

    useFocusEffect(
        useCallback(() => {
            navigation.setOptions({
                header: ({ route, options }) => {
                    const title = getHeaderTitle(options, route.name);

                    // Selection mode owns the header: the count replaces the screen title and
                    // "Cancel" is the only way out, so the filter action is deliberately dropped.
                    if (isSelectionMode) {
                        return (
                            <ScreenHeader
                                useSmallLabel
                                label={i18n.t("screens.user.missions.selection_title", {
                                    count: selectedMissionIds.length,
                                })}
                                leftComponent={
                                    <HeaderButton
                                        label={i18n.t("cancel")}
                                        onPress={exitSelectionMode}
                                    />
                                }
                            />
                        );
                    }

                    return (
                        <ScreenHeader
                            actions={
                                hasFilterOptions
                                    ? [
                                          {
                                              icon: "menu",
                                              label: "test",
                                              onPress: () => {
                                                  setIsMenuOpen(true);
                                              },
                                          },
                                      ]
                                    : []
                            }
                            actionButtonWithBadge={false}
                            label={title}
                        />
                    );
                },
            });
        }, [
            navigation,
            hasFilterOptions,
            isSelectionMode,
            selectedMissionIds.length,
            i18n,
            exitSelectionMode,
        ]),
    );

    const items = useMemo(() => {
        if (!filteredMissions) {
            return [];
        }

        return [...filteredMissions].map<ListGroupItem<string>>((mission) => {
            const title =
                profileType === ProfileType.Client
                    ? mission.workerInfo.name
                    : mission.clientInfo.name;
            const avatarPath =
                profileType === ProfileType.Client
                    ? mission.workerInfo.avatarUrl
                    : mission.clientInfo.logoUrl;
            const avatarEmail =
                profileType === ProfileType.Client
                    ? mission.workerInfo.email
                    : mission.clientInfo.email;

            const jobTitle = getMissionJobTitleDisplay(mission);
            const missionName = mission.name;
            const displayAttributes = getMissionDisplayAttributes(
                mission,
                profileType,
                mission.terminationInfo as TerminationInfo,
            );
            const description = `${missionName || jobTitle || ""}\n${displayAttributes.durationInfo}`;
            const isCurrentUserOwner = mission.clientInfo.id === clientProfile?.id;
            return {
                value: mission.id,
                title,
                avatarPath,
                avatarEmail,
                description,
                labels: [
                    {
                        text: displayAttributes.shortStatus,
                        mode: displayAttributes.shortStatuslabelMode || ("default" as const),
                    },
                    ...(displayAttributes.hasPendingWorkerSignatureForAmendment
                        ? [
                              {
                                  text: i18n.t(
                                      "screens.user.contract_details.amendment_awaiting_worker_signature",
                                  ),
                                  mode: "warning" as const,
                              },
                          ]
                        : []),
                    ...(displayAttributes.hasPendingCurrentUserSignatureForAmendment
                        ? [
                              {
                                  text: i18n.t(
                                      "screens.user.contract_details.amendment_awaiting_your_signature",
                                  ),
                                  mode: "warning" as const,
                              },
                          ]
                        : []),
                    ...(isCurrentUserOwner
                        ? [
                              {
                                  text: i18n.t("mission.details.owner"),
                                  mode: "secondary" as const,
                              },
                          ]
                        : []),
                ],
                tags: [
                    formatDate(mission.createdAt),
                    ...(profileType === ProfileType.Worker ? [mission.clientInfo.profileName] : []),
                ],
                useBigAvatarOrIcon: true,
                onPress: () => {
                    onSelectMission(mission.id);
                },
            };
        });
    }, [
        filteredMissions,
        profileType,
        getMissionDisplayAttributes,
        clientProfile?.id,
        i18n,
        formatDate,
        onSelectMission,
    ]);

    const dynamicButtonProps = useDynamicHeaderButton(
        isSelectionMode
            ? // Selection mode has its own bottom action; the "create mission" CTA would compete
              // with it and lead out of the flow.
              undefined
            : profileType === ProfileType.Client && hasTalent && !missionsError && items.length > 0
              ? {
                    title: i18n.t("screens.user.missions.create_new_mission"),
                    shortTitle: i18n.t("mission.nav_title"),
                    onClick: onCreateMission,
                }
              : profileType === ProfileType.Worker && !missionsError && items.length > 0 && !isLarge
                ? {
                      title: i18n.t("screens.user.missions.invite_client"),
                      shortTitle: i18n.t("screens.user.missions.invite_client_short"),
                      onClick: onInviteClient,
                      icon: "arrow-right-from-bracket",
                  }
                : undefined,
    );

    // In selection mode only the signable contracts are listed: the others cannot be batch-signed,
    // so showing them would only offer rows the flow has to refuse. A row toggles its checkbox
    // instead of opening the mission — navigating away mid-flow would throw the selection away.
    const selectableItems = useMemo(
        () =>
            isSelectionMode
                ? items
                      .filter((item) => signableMissionIds.has(item.value))
                      .map((item) => {
                          const isSelected = selectedMissionIds.includes(item.value);

                          return {
                              ...item,
                              // No chevron in selection mode: the row no longer navigates anywhere.
                              rightActionMode: "none" as const,
                              // Inside the row rather than around it, so the row keeps its own
                              // padding and its bottom border still spans the full width.
                              leadingComponent: (
                                  <Pressable
                                      style={styles.selectionCheckbox}
                                      onPress={() => toggleMissionSelection(item.value)}
                                  >
                                      <Icon
                                          name={isSelected ? "square-check" : "square"}
                                          size={SELECTION_CHECKBOX_SIZE}
                                          color={isSelected ? colors.secondary : colors.body}
                                      />
                                  </Pressable>
                              ),
                              onPress: () => toggleMissionSelection(item.value),
                          };
                      })
                : items,
        [
            isSelectionMode,
            items,
            signableMissionIds,
            selectedMissionIds,
            toggleMissionSelection,
            styles.selectionCheckbox,
            colors.secondary,
            colors.body,
        ],
    );

    const renderRow = useListGroupRenderItem(selectableItems, {
        isItemsCheckable: false,
        itemStyle: styles.listGroupItem,
    });

    const signSelectedButton = useMemo(
        () => (
            <Button
                key="sign-selected"
                size="large"
                buttonMode="secondary"
                leftIcon="edit"
                leftIconSize={20}
                style={styles.signButton}
                disabled={selectedMissionIds.length === 0}
                label={i18n.t("screens.user.missions.sign_selected", {
                    count: selectedMissionIds.length,
                })}
                onPress={() => {
                    navigation.navigate("MissionsMultiSign", { missionIds: selectedMissionIds });
                }}
            />
        ),
        [i18n, navigation, selectedMissionIds, styles.signButton],
    );

    // The list is virtualized when populated; loading/empty/error keep the ScrollView path.
    // Counted on the displayed rows, which selection mode narrows down to the signable ones.
    const showList = !isInitialLoading && !missionsError && selectableItems.length > 0;

    // Trailing "know more" link, shown after the list in both modes.
    const knowMoreLink = (
        <>
            <Gap gap={1} />
            <LinkSmallTextBold
                style={{ alignSelf: "center" }}
                onPress={() => {
                    if (profileType === ProfileType.Client) {
                        void presentArticleId("9983409");
                    } else if (profileType === ProfileType.Worker) {
                        void presentArticleId("9983402");
                    }
                }}
            >
                {i18n.t("screens.user.missions.know_more_about_missions")}
            </LinkSmallTextBold>
            <Gap gap={1} />
        </>
    );

    return (
        <>
            <SelectChooser
                visible={isMenuOpen && hasFilterOptions}
                searchItems={filterItems}
                topComponent={filtersTopComponent}
                topComponentStyle={{ borderBottomWidth: 0 }}
                bottomComponent={filtersBottomComponent}
                dismissOnPress={false}
                onDismiss={() => {
                    setIsMenuOpen(false);
                }}
            />
            {/* `flex: 1` and not `height: "100%"`: the screen content is a sibling of the custom
                header inside the stack's screen container, so a full-height child overflows by the
                header's height and pushes the bottom action bar under the tab bar. */}
            <View style={{ flex: 1 }}>
                <ScreenCard
                    refresh={refresh}
                    isContentContainerFlex={!missionsError && (!items.length || isInitialLoading)}
                    headband={
                        isSelectionMode
                            ? {
                                  mode: "info",
                                  text: i18n.t("screens.user.missions.selection_banner"),
                              }
                            : missionsError
                              ? buildErrorHeadband(
                                    i18n.t("screens.user.missions.cannot_load_missions"),
                                )
                              : emailVerificationHeadband
                    }
                    {...dynamicButtonProps}
                    buttons={isSelectionMode ? [signSelectedButton] : undefined}
                    virtualizedItems={showList ? selectableItems : undefined}
                    renderItem={showList ? renderRow : undefined}
                    virtualizedFooter={showList ? knowMoreLink : undefined}
                >
                    {showList ? undefined : isInitialLoading ? (
                        <Loading />
                    ) : (
                        <>
                            {missionsError ? undefined : items.length === 0 ? (
                                <EmptyMissions
                                    onCreateMission={onCreateMission}
                                    onInviteClient={onInviteClient}
                                    hasTalent={hasTalent}
                                    hasClient={hasClient}
                                />
                            ) : null}
                            {knowMoreLink}
                        </>
                    )}
                </ScreenCard>
            </View>
        </>
    );
}

const useStyles = () => {
    const { gridSize, marginSize, colors } = useTheme();
    const isLarge = useIsScreenSizeMin("large");

    return useMemo(
        () =>
            StyleSheet.create({
                startButton: {
                    marginHorizontal: gridSize,
                    alignSelf: !isLarge ? "stretch" : "center",
                },
                listGroupItem: {
                    paddingBottom: gridSize,
                },
                fab: { position: "absolute", margin: gridSize / 2, right: 0, bottom: 0 },
                filtersHeader: {
                    marginHorizontal: -marginSize,
                },
                filtersSeparator: {
                    height: gridSize / 4,
                    backgroundColor: colors.lighterGrey,
                },
                filtersIntro: {
                    paddingHorizontal: marginSize,
                    marginTop: gridSize / 2,
                },
                selectionCheckbox: {
                    // Sits at the top of the row and centres on the avatar next to it, rather than
                    // on the full row height, which the labels below make much taller.
                    alignSelf: "flex-start",
                    paddingTop: (SELECTION_ROW_AVATAR_SIZE - SELECTION_CHECKBOX_SIZE) / 2,
                    marginRight: gridSize / 2,
                },
                signButton: {
                    alignSelf: "stretch",
                },
            }),
        [gridSize, isLarge, marginSize, colors.lighterGrey],
    );
};
